import { BulkSpreadsheet, parseBool, parseNum, type BulkColumn } from '../shared/BulkSpreadsheet';
import { productService } from '@kss/api';
import type { ProductCreate } from '@kss/api';
import { loadCategoryOptions, loadStoreOptions } from '../shared/formLoaders';

/**
 * Bulk-imports products via a paste-friendly spreadsheet grid. The
 * `imageUrl` column was missing previously — admins importing 20+
 * rows had no way to attach an image per product without re-editing
 * each row through the New Product form afterwards.
 *
 * Bug ref: AI Agent 2026-05-14 — "multiple product with image
 * update in spread sheet bulk not working".
 *
 * Accepted image formats (paste any of these into the `Image URL` cell):
 *   - Full HTTPS URL — e.g. `https://cdn.example.com/rice.jpg`
 *   - Tenant-relative path — e.g. `/uploads/<tenantId>/product/rice.jpg`
 *     (path-only URLs get resolved by the existing `toAbsoluteImageUrl`
 *     adapter at render time)
 *
 * The live API's `Product` row only stores ONE image (`imageUrl`).
 * Multi-image support needs a backend `/ProductImage` table — until
 * then the single field is what we persist. Sample columns include a
 * placeholder URL so the first row demonstrates the format.
 */
const columns: BulkColumn<ProductCreate>[] = [
  { key: 'name', header: 'Name', required: true, sample: 'Basmati Rice 5kg' },
  { key: 'sku', header: 'SKU', required: true, sample: 'RICE-BSM-5KG' },
  { key: 'barcode', header: 'Barcode' },
  { key: 'hsnCode', header: 'HSN code' },
  { key: 'brand', header: 'Brand', sample: 'India Gate' },
  { key: 'unit', header: 'Unit', sample: 'pcs' },
  { key: 'costPrice', header: 'Cost', dataType: 'number', parse: parseNum as never, sample: '100' },
  { key: 'sellingPrice', header: 'Selling', dataType: 'number', parse: parseNum as never, sample: '150', required: true },
  { key: 'mrpPrice', header: 'MRP', dataType: 'number', parse: parseNum as never, sample: '180' },
  { key: 'taxRate', header: 'Tax %', dataType: 'number', parse: parseNum as never, sample: '18' },
  { key: 'weight', header: 'Weight', dataType: 'number', parse: parseNum as never },
  {
    key: 'imageUrl',
    header: 'Image URL',
    sample: 'https://cdn.example.com/rice-5kg.jpg',
  },
  { key: 'categoryId', header: 'Category', dataType: 'lookup', loadOptions: loadCategoryOptions },
  { key: 'storeId', header: 'Store', dataType: 'lookup', loadOptions: loadStoreOptions },
  { key: 'isActive', header: 'Active', dataType: 'boolean', parse: parseBool as never, sample: 'true' },
];

/**
 * Normalise an Image URL cell — trim whitespace, drop the cell if
 * blank. Leaves paste-as-URL values intact (the storefront's
 * `toAbsoluteImageUrl` adapter handles relative vs absolute URLs).
 */
function normaliseImageUrl(raw: string | undefined | null): string | undefined {
  const v = (raw ?? '').trim();
  return v.length === 0 ? undefined : v;
}

export default function BulkProduct() {
  return (
    <BulkSpreadsheet<ProductCreate>
      title="Bulk import products" entityLabel="product" backPath="/panel/products" columns={columns} module="Product"
      onImport={async (rows) => {
        const payload = rows.map((r) => ({
          ...r,
          sellingPrice: Number(r.sellingPrice ?? 0),
          isActive: r.isActive ?? true,
          /* Blank cells must become `undefined` so the backend
             strict-mode binder doesn't reject the row with
             "Unknown field 'imageUrl'" / "imageUrl is required". */
          imageUrl: normaliseImageUrl(r.imageUrl as string | undefined),
        }));
        const created = await productService.create(payload);
        return { created: Array.isArray(created) ? created.length : payload.length };
      }}
    />
  );
}
